import { useCallback, useEffect, useMemo, useState } from "react";
import { api, downloadBlob } from "../api.js";
import ErrorBanner from "../components/ErrorBanner.jsx";

/**
 * Admin console.
 *
 *  Tab 1 — Users: list + create + edit (role, supervisor, active toggle).
 *  Tab 2 — Export: monthly CSV dump for payroll.
 *  Tab 3 — Audit: paginated read-only view of audit_log (who did what,
 *          when, and what changed). Read-only — the server writes the rows.
 *  Tab 4 — Settings: mileage rate per mile + template info.
 *
 * Only admin users can reach these endpoints; the backend enforces this. The
 * UI hides the tab otherwise.
 */
export default function Admin() {
  const [tab, setTab] = useState("users"); // users | export | audit | settings

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold text-slate-900">Admin</h1>
      </div>

      <div className="flex flex-wrap gap-2 text-sm">
        <SubTab label="Users" active={tab === "users"} onClick={() => setTab("users")} />
        <SubTab label="Export" active={tab === "export"} onClick={() => setTab("export")} />
        <SubTab label="Audit" active={tab === "audit"} onClick={() => setTab("audit")} />
        <SubTab label="Settings" active={tab === "settings"} onClick={() => setTab("settings")} />
      </div>

      {tab === "users" && <UsersTab />}
      {tab === "export" && <ExportTab />}
      {tab === "audit" && <AuditTab />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}

function SubTab({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={
        "tap rounded-full px-4 py-1.5 font-medium " +
        (active
          ? "bg-endeavor-600 text-white"
          : "border border-slate-300 bg-white text-slate-700")
      }
    >
      {label}
    </button>
  );
}

function UsersTab() {
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // user object or null

  const refresh = useCallback(async () => {
    setErr(null);
    try {
      const d = await api.adminListUsers();
      setUsers(d.users);
    } catch (e) {
      setErr(e.message);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const supervisors = useMemo(
    () =>
      (users || []).filter(
        (u) => (u.role === "supervisor" || u.role === "admin") && u.active
      ),
    [users]
  );

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button className="btn-primary" onClick={() => setAdding(true)}>
          + Add user
        </button>
      </div>

      <ErrorBanner message={err} onRetry={refresh} />

      {users === null && !err && (
        <div className="text-sm text-slate-500">Loading…</div>
      )}

      <ul className="space-y-2">
        {users?.map((u) => (
          <li key={u.id}>
            <button
              onClick={() => setEditing(u)}
              className="card w-full text-left active:bg-slate-50"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    {u.fullName}
                    {!u.active && (
                      <span className="ml-2 text-xs font-normal text-slate-400">
                        (inactive)
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                  {u.supervisorName && (
                    <div className="mt-1 text-xs text-slate-500">
                      Reports to {u.supervisorName}
                    </div>
                  )}
                  {/* Workers need a supervisor or their submissions can't
                      be routed. Admins/supervisors don't. This flag matches
                      the one SupervisorReview shows on the pending queue for
                      unassigned submissions — same principle, earlier catch. */}
                  {u.active && u.role === "worker" && !u.supervisorName && (
                    <div className="mt-1 text-xs text-amber-700">
                      ⚠ No supervisor assigned
                    </div>
                  )}
                  {/* If the assigned supervisor can't approve — either
                      deactivated or demoted to worker — the worker's pending
                      submissions orphan silently. Surface both failure modes
                      with a single warning the admin can act on. */}
                  {u.active && u.supervisorName && u.supervisorCanApprove === false && (
                    <div className="mt-1 text-xs text-amber-700">
                      ⚠ Supervisor {u.supervisorName}
                      {u.supervisorActive === false
                        ? " is deactivated"
                        : ` is no longer a supervisor (role: ${u.supervisorRole || "?"})`}
                      {" — reassign this worker"}
                    </div>
                  )}
                </div>
                <RoleBadge role={u.role} />
              </div>
            </button>
          </li>
        ))}
      </ul>

      {adding && (
        <UserForm
          mode="create"
          supervisors={supervisors}
          onClose={() => setAdding(false)}
          onSaved={async () => {
            setAdding(false);
            await refresh();
          }}
        />
      )}
      {editing && (
        <UserForm
          mode="edit"
          user={editing}
          supervisors={supervisors}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function RoleBadge({ role }) {
  const styles = {
    admin: "bg-purple-50 text-purple-700",
    supervisor: "bg-sky-50 text-sky-700",
    worker: "bg-slate-100 text-slate-700",
  };
  return (
    <span
      className={
        "rounded-full px-2 py-0.5 text-xs font-medium capitalize " +
        (styles[role] || styles.worker)
      }
    >
      {role}
    </span>
  );
}

function UserForm({ mode, user, supervisors, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [email, setEmail] = useState(user?.email || "");
  const [fullName, setFullName] = useState(user?.fullName || "");
  const [role, setRole] = useState(user?.role || "worker");
  const [supervisorId, setSupervisorId] = useState(user?.supervisorId || "");
  const [active, setActive] = useState(user?.active ?? true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setErr(null);
    setBusy(true);
    try {
      if (isEdit) {
        await api.adminUpdateUser(user.id, {
          fullName: fullName.trim(),
          role,
          supervisorId: supervisorId || null,
          active,
        });
      } else {
        if (!email.trim() || !fullName.trim())
          throw new Error("Email and name are required.");
        await api.adminCreateUser({
          email: email.trim().toLowerCase(),
          fullName: fullName.trim(),
          role,
          supervisorId: supervisorId || null,
        });
      }
      await onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 sm:items-center"
      onClick={onClose}
    >
      <div
        className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-white p-4 shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">
            {isEdit ? "Edit user" : "Add user"}
          </h2>
          <button
            onClick={onClose}
            className="tap rounded-md px-3 text-slate-500 active:bg-slate-100"
          >
            ✕
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="label">Email</label>
            <input
              className="field"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isEdit}
              placeholder="you@endeavorhs.org"
            />
            {isEdit && (
              <p className="mt-1 text-xs text-slate-500">
                Email can't change once set (it's the login key).
              </p>
            )}
          </div>
          <div>
            <label className="label">Full name (as it appears on the form)</label>
            <input
              className="field"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Doe, LMSW"
            />
          </div>
          <div>
            <label className="label">Role</label>
            <div className="grid grid-cols-3 gap-2">
              {["worker", "supervisor", "admin"].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className={
                    "tap rounded-lg border px-3 py-2 text-sm font-medium capitalize " +
                    (role === r
                      ? "border-endeavor-500 bg-endeavor-50 text-endeavor-700"
                      : "border-slate-300 bg-white text-slate-700")
                  }
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
          {role === "worker" && (
            <div>
              <label className="label">Supervisor</label>
              <select
                className="field"
                value={supervisorId}
                onChange={(e) => setSupervisorId(e.target.value)}
              >
                <option value="">(none)</option>
                {supervisors
                  .filter((s) => !user || s.id !== user.id)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.fullName} — {s.email}
                    </option>
                  ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">
                The supervisor who counter-signs this worker's monthly report.
              </p>
            </div>
          )}
          {isEdit && (
            <label className="flex items-center gap-3 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-5 w-5"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
              <span>Active (can sign in and submit mileage)</span>
            </label>
          )}

          <ErrorBanner message={err} />

          <button
            className="btn-primary w-full"
            onClick={save}
            disabled={busy}
          >
            {busy ? "Saving…" : isEdit ? "Save changes" : "Create user"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Admin → Export tab. Build a CSV of monthly submissions for payroll /
 * accounting. Two modes:
 *   - Summary: one row per submission (worker, totals, status, timestamps)
 *   - Detail:  one row per mileage entry (trip-level audit trail)
 *
 * Date range is a year (required) with optional month. Status filters to
 * approved by default but the admin can include submitted-but-not-yet-approved
 * rows, or pull everything.
 */
function ExportTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [allYear, setAllYear] = useState(false);
  const [detail, setDetail] = useState(false);
  const [status, setStatus] = useState("approved");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function run() {
    setErr(null);
    setBusy(true);
    try {
      const { blob, filename } = await api.adminExportCsv({
        year: Number(year),
        month: allYear ? null : Number(month),
        detail,
        status,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Defer revoke so the download actually starts; some browsers race.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-4">
      <div>
        <div className="text-sm font-semibold text-slate-700">
          Monthly CSV export
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Download a spreadsheet of submissions for payroll reconciliation.
          Summary gives one row per report; Detail gives one row per trip.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Year</label>
          <input
            className="field tabular-nums"
            inputMode="numeric"
            value={year}
            onChange={(e) => setYear(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Month</label>
          <select
            className="field"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            disabled={allYear}
          >
            {[
              "January","February","March","April","May","June",
              "July","August","September","October","November","December",
            ].map((n, i) => (
              <option key={i + 1} value={i + 1}>
                {String(i + 1).padStart(2, "0")} — {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={allYear}
          onChange={(e) => setAllYear(e.target.checked)}
        />
        Export the full year (ignore month)
      </label>

      <div>
        <label className="label">Format</label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setDetail(false)}
            className={
              "tap rounded-lg border px-3 py-2 text-sm font-medium " +
              (!detail
                ? "border-endeavor-500 bg-endeavor-50 text-endeavor-700"
                : "border-slate-300 bg-white text-slate-700")
            }
          >
            Summary (per report)
          </button>
          <button
            type="button"
            onClick={() => setDetail(true)}
            className={
              "tap rounded-lg border px-3 py-2 text-sm font-medium " +
              (detail
                ? "border-endeavor-500 bg-endeavor-50 text-endeavor-700"
                : "border-slate-300 bg-white text-slate-700")
            }
          >
            Detail (per trip)
          </button>
        </div>
      </div>

      <div>
        <label className="label">Status</label>
        <select
          className="field"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="approved">Approved only</option>
          <option value="submitted">Submitted (not yet approved)</option>
          <option value="all">All (submitted + approved)</option>
        </select>
      </div>

      <ErrorBanner message={err} />

      <button className="btn-primary w-full" onClick={run} disabled={busy}>
        {busy ? "Preparing…" : "Download CSV"}
      </button>
    </div>
  );
}

/**
 * Admin → Audit tab. Paginated read-only view of `audit_log`. Supports
 * filtering by entity type (submission | user | config) and by action
 * (create, update, approve, return, submit, etc.). The server parses the
 * JSON diff blobs before returning so we can render them directly.
 *
 * Page size kept at 50 — the list is high-cardinality (every entry edit is
 * a row) and we don't want to blow up the mobile-first UI with a thousand
 * rows.
 */
const AUDIT_PAGE_SIZE = 50;

function AuditTab() {
  const [rows, setRows] = useState(null);
  const [offset, setOffset] = useState(0);
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  // Free-text actor-email filter. Debounced below so we don't thrash the
  // endpoint on every keystroke. Server does a case-insensitive exact match.
  const [actorEmailInput, setActorEmailInput] = useState("");
  const [actorEmail, setActorEmail] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Debounce the actor filter so typing "jane@endeavor.org" doesn't fire
  // one request per character. 400ms is a reasonable trade between
  // responsiveness and request volume for a rarely-touched admin tab.
  useEffect(() => {
    const t = setTimeout(() => setActorEmail(actorEmailInput.trim()), 400);
    return () => clearTimeout(t);
  }, [actorEmailInput]);

  async function exportCsv() {
    setErr(null);
    setExporting(true);
    try {
      const { blob, filename } = await api.adminExportAuditCsv({
        entityType: entityType || undefined,
        action: action || undefined,
        actorEmail: actorEmail || undefined,
      });
      downloadBlob({ blob, filename });
    } catch (e) {
      setErr(e.message);
    } finally {
      setExporting(false);
    }
  }

  const refresh = useCallback(
    async (nextOffset = offset) => {
      setErr(null);
      setBusy(true);
      try {
        const d = await api.adminListAudit({
          limit: AUDIT_PAGE_SIZE,
          offset: nextOffset,
          entityType: entityType || undefined,
          action: action || undefined,
          actorEmail: actorEmail || undefined,
        });
        setRows(d.entries || []);
        setOffset(nextOffset);
      } catch (e) {
        setErr(e.message);
      } finally {
        setBusy(false);
      }
    },
    [offset, entityType, action, actorEmail]
  );

  // Refresh whenever filters change. We reset to page 0 when the filter
  // changes so the user doesn't land on an empty page in the new view.
  useEffect(() => {
    refresh(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, action, actorEmail]);

  const hasPrev = offset > 0;
  const hasNext = (rows?.length ?? 0) === AUDIT_PAGE_SIZE;

  return (
    <div className="space-y-3">
      <div className="card space-y-3">
        <div>
          <div className="text-sm font-semibold text-slate-700">Audit log</div>
          <p className="mt-1 text-xs text-slate-500">
            Read-only record of every submission, approval, return, and user
            change. Useful for payroll reconciliation or answering "who changed
            this and when?"
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Entity</label>
            {/* Values must match the strings stored in audit_log.entity_type
                by the server (see db.js / server.js audit() calls). */}
            <select
              className="field"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
            >
              <option value="">All</option>
              <option value="mileage_submission">Submission</option>
              <option value="mileage_entry">Trip entry</option>
              <option value="user">User</option>
              <option value="app_config">App config</option>
            </select>
          </div>
          <div>
            <label className="label">Action</label>
            <select
              className="field"
              value={action}
              onChange={(e) => setAction(e.target.value)}
            >
              <option value="">All</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="submit">Submit</option>
              <option value="approve">Approve</option>
              <option value="return">Return</option>
              <option value="admin_create">Admin create user</option>
              <option value="admin_update">Admin update user</option>
              <option value="admin_config">Admin config change</option>
            </select>
          </div>
        </div>
        <div>
          <label className="label" htmlFor="actor-email">
            Actor email
          </label>
          <input
            id="actor-email"
            type="email"
            inputMode="email"
            autoComplete="off"
            className="field"
            value={actorEmailInput}
            onChange={(e) => setActorEmailInput(e.target.value)}
            placeholder="jane@endeavorhs.org"
          />
          <p className="mt-1 text-[11px] text-slate-500">
            Exact email match (case-insensitive). Leave blank for all actors.
          </p>
        </div>
        {/* CSV export honors the current filter selection so compliance
            reviewers can scope an export ("all returns in 2025") before
            downloading, instead of grepping through a full-year dump. */}
        <div className="flex items-center justify-between gap-2 pt-1">
          <p className="text-xs text-slate-500">
            Export respects the filters above.
          </p>
          <button
            type="button"
            className="btn-secondary text-xs"
            disabled={exporting}
            onClick={exportCsv}
          >
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>

      <ErrorBanner message={err} onRetry={() => refresh(offset)} />

      {rows === null && !err && (
        <div className="text-sm text-slate-500">Loading audit log…</div>
      )}
      {rows && rows.length === 0 && !busy && (
        <div className="rounded-xl border-2 border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          No audit entries match your filters.
        </div>
      )}

      {rows && rows.length > 0 && (
        <ul className="space-y-2">
          {rows.map((r) => (
            <AuditRow key={r.id} row={r} />
          ))}
        </ul>
      )}

      {/* Pagination — matched to server LIMIT/OFFSET semantics. */}
      {(hasPrev || hasNext) && (
        <div className="flex items-center justify-between pt-1 text-sm">
          <button
            className="btn-secondary"
            disabled={!hasPrev || busy}
            onClick={() => refresh(Math.max(0, offset - AUDIT_PAGE_SIZE))}
          >
            ‹ Prev
          </button>
          <span className="text-xs text-slate-500 tabular-nums">
            {offset + 1}–{offset + (rows?.length ?? 0)}
          </span>
          <button
            className="btn-secondary"
            disabled={!hasNext || busy}
            onClick={() => refresh(offset + AUDIT_PAGE_SIZE)}
          >
            Next ›
          </button>
        </div>
      )}
    </div>
  );
}

function AuditRow({ row }) {
  const [expanded, setExpanded] = useState(false);
  const hasDiff =
    (row.before && Object.keys(row.before).length > 0) ||
    (row.after && Object.keys(row.after).length > 0);
  const summary = summarizeAuditRow(row);

  return (
    <li className="card">
      <button
        className="w-full text-left"
        onClick={() => hasDiff && setExpanded((v) => !v)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                {row.entityType}
              </span>
              <span className="rounded-full bg-endeavor-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-endeavor-700">
                {row.action}
              </span>
              <span className="truncate text-xs text-slate-500">
                #{String(row.entityId).slice(0, 8)}
              </span>
            </div>
            <div className="mt-1 text-sm font-medium text-slate-900">
              {row.actorName || row.actorEmail || "(unknown actor)"}
            </div>
            {/* Plain-English summary for common actions (rate change,
                role/active toggle, return reason) so admins don't need to
                expand every row to scan the feed. Falls through to the raw
                JSON-on-expand for anything we don't have a formatter for. */}
            {summary && (
              <div className="mt-1 text-xs text-slate-700">{summary}</div>
            )}
            <div className="text-xs text-slate-500">
              {formatAuditTime(row.createdAt)}
            </div>
          </div>
          {hasDiff && (
            <span className="text-slate-400">{expanded ? "▾" : "▸"}</span>
          )}
        </div>
      </button>
      {expanded && hasDiff && (
        <div className="mt-3 space-y-2 border-t border-slate-100 pt-3 text-xs">
          {row.before && Object.keys(row.before).length > 0 && (
            <pre className="max-h-48 overflow-auto rounded bg-red-50/50 p-2 text-red-900">
              {"- " + JSON.stringify(row.before, null, 2).replace(/\n/g, "\n  ")}
            </pre>
          )}
          {row.after && Object.keys(row.after).length > 0 && (
            <pre className="max-h-48 overflow-auto rounded bg-emerald-50/60 p-2 text-emerald-900">
              {"+ " + JSON.stringify(row.after, null, 2).replace(/\n/g, "\n  ")}
            </pre>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Plain-English summary of common audit rows for the Admin > Audit tab.
 *
 * Returns null if the row doesn't match a known shape — the raw JSON
 * before/after dump in the expanded view is still available as the
 * authoritative record.
 *
 * Keeping this formatter-per-action map here (rather than on the server)
 * means the audit log itself stays canonical — the API returns the raw
 * diff and the UI layers on a nicer scan view. Adding a new action later
 * doesn't require a migration.
 */
function summarizeAuditRow(row) {
  const { entityType, action, entityId, before, after } = row;
  if (entityType === "app_config" && entityId === "mileage_rate_per_mile") {
    // Loop 12 captures the prior value as before.value; pre-loop-12 rows
    // only have after.value, so degrade gracefully.
    const a = after?.value;
    const b = before?.value;
    if (typeof a === "number") {
      if (typeof b === "number") {
        return `Rate: $${b.toFixed(3)}/mi → $${a.toFixed(3)}/mi`;
      }
      return `Rate set to $${a.toFixed(3)}/mi`;
    }
  }
  if (entityType === "mileage_submission" && action === "return") {
    const reason = after?.reason || before?.reason;
    if (reason) return `Returned — "${truncate(String(reason), 120)}"`;
  }
  if (entityType === "user" && action === "admin_update" && after) {
    // Loop 20 added the `before` payload; pre-Loop-20 rows still have
    // before=null, so every branch falls through to the simpler format.
    const b = before || {};
    const parts = [];
    if ("active" in after) {
      // Flip: was Jane active before this admin tweak? Show "Activated"
      // only when the state actually changed; no-op toggles get "Still active"
      // suppressed entirely because they produce no actionable info.
      if ("active" in b && !!b.active === !!after.active) {
        // no-op toggle — skip
      } else {
        parts.push(after.active ? "Activated" : "Deactivated");
      }
    }
    if ("role" in after) {
      if ("role" in b && b.role !== after.role) {
        parts.push(`Role: ${b.role} → ${after.role}`);
      } else {
        parts.push(`Role → ${after.role}`);
      }
    }
    if ("supervisorId" in after) {
      const hadSup = "supervisorId" in b ? !!b.supervisorId : null;
      const hasSup = !!after.supervisorId;
      if (hadSup === null) {
        parts.push(hasSup ? "Supervisor set" : "Supervisor cleared");
      } else if (hadSup && !hasSup) {
        parts.push("Supervisor cleared");
      } else if (!hadSup && hasSup) {
        parts.push("Supervisor set");
      } else if (hadSup && hasSup && b.supervisorId !== after.supervisorId) {
        parts.push("Supervisor changed");
      }
    }
    if ("fullName" in after) {
      if ("fullName" in b && b.fullName !== after.fullName) {
        parts.push(`Name: ${b.fullName} → ${after.fullName}`);
      } else {
        parts.push(`Name → ${after.fullName}`);
      }
    }
    if (parts.length > 0) return parts.join(" · ");
  }
  if (entityType === "user" && action === "admin_create" && after?.email) {
    return `Added ${after.fullName || after.email} as ${after.role || "worker"}`;
  }
  if (entityType === "mileage_entry") {
    // Trip-entry audits dominate the log by volume. Before this case,
    // admins had to expand every row to answer "what trip did Jane
    // delete yesterday?" — the raw JSON is long and has cents-vs-dollars
    // field drift between create (user input) and update/delete (db row).
    const describe = (payload) => {
      if (!payload) return null;
      // Create audits use user-facing field names; update/delete use DB column
      // names. Accept both so this works across all three action verbs.
      const date = payload.date || payload.trip_date;
      const origin = payload.origin;
      const destination = payload.destination;
      const miles = payload.miles;
      if (!origin || !destination) return null;
      const milesStr = Number.isFinite(Number(miles))
        ? ` · ${Number(miles).toFixed(1)} mi`
        : "";
      const datePart = date ? `${date} — ` : "";
      return `${datePart}${origin} → ${destination}${milesStr}`;
    };
    if (action === "create") {
      const s = describe(after);
      if (s) return `Added trip: ${truncate(s, 120)}`;
    } else if (action === "delete") {
      const s = describe(before);
      if (s) return `Deleted trip: ${truncate(s, 120)}`;
    } else if (action === "update") {
      // Prefer the latest values for the "now" description; fall back to
      // before for fields the patch didn't touch.
      const merged = { ...(before || {}), ...(after || {}) };
      const s = describe(merged);
      if (s) return `Edited trip: ${truncate(s, 120)}`;
    }
  }
  return null;
}

function truncate(s, n) {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function formatAuditTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function SettingsTab() {
  const [cfg, setCfg] = useState(null);
  const [rate, setRate] = useState("");
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rateHistory, setRateHistory] = useState(null);

  // Extracted so the ErrorBanner retry button can invoke the same
  // fetch path — otherwise a network blip strands the admin on
  // "Loading settings…" with no recourse but a full page refresh.
  const loadConfig = useCallback(async () => {
    setErr(null);
    try {
      const c = await api.adminGetConfig();
      setCfg(c);
      setRate(String(c.mileageRatePerMile));
    } catch (e) {
      setErr(e.message);
    }
  }, []);

  // Pull the last N rate-change audit rows. Lives here (rather than making
  // admins switch to the Audit tab + apply filters) because "what's the
  // history of our mileage rate?" is the #1 Settings-tab question during
  // payroll reconciliation. Filter is done client-side by entityId since
  // the audit endpoint doesn't accept it as a query param — the volume
  // of admin_config rows is low (expected single-digits per year) so this
  // doesn't blow up the response.
  const loadRateHistory = useCallback(async () => {
    try {
      const d = await api.adminListAudit({
        limit: 50,
        entityType: "app_config",
        action: "admin_config",
      });
      const filtered = (d.entries || []).filter(
        (r) => r.entityId === "mileage_rate_per_mile"
      );
      setRateHistory(filtered);
    } catch {
      // Soft-fail: the history card is a nice-to-have, shouldn't block the
      // primary Save-rate flow on the screen above it.
      setRateHistory([]);
    }
  }, []);

  useEffect(() => {
    loadConfig();
    loadRateHistory();
  }, [loadConfig, loadRateHistory]);

  async function save() {
    setErr(null);
    setSaved(false);
    setBusy(true);
    try {
      const n = Number(rate);
      if (!Number.isFinite(n) || n <= 0)
        throw new Error("Enter a valid rate (dollars per mile).");
      const next = await api.adminSetConfig({ mileageRatePerMile: n });
      setCfg(next);
      setSaved(true);
      // Surface the change we just made in the history card below.
      loadRateHistory();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!cfg) {
    // Surface an errored load with retry instead of a permanent "Loading…"
    // spinner. Using the shared banner keeps the behavior parallel with
    // the other Admin tabs.
    return (
      <div className="space-y-3">
        <ErrorBanner message={err} onRetry={loadConfig} />
        {!err && (
          <div className="text-sm text-slate-500">Loading settings…</div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="card space-y-3">
        <div>
          <div className="text-sm font-semibold text-slate-700">
            Mileage rate
          </div>
          <p className="text-xs text-slate-500">
            Dollars per mile. Used for new submissions. Existing approved
            reports are not retroactively changed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-500">$</span>
          <input
            className="field tabular-nums"
            inputMode="decimal"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
          />
          <span className="text-sm text-slate-500">/ mile</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">
            Current: ${cfg.mileageRatePerMile}/mile
          </span>
          <button className="btn-primary" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
        {saved && (
          <div className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            Rate updated.
          </div>
        )}
        <ErrorBanner message={err} />
      </div>

      {/* Rate-change history — scannable audit slice so an admin doing
          year-end reconciliation can answer "when did the rate change from
          $0.655 to $0.67?" without hopping to the Audit tab and manually
          filtering. Pre-Loop-12 rows won't carry `before.value`; we fall
          back to "Set to $X" for those. */}
      <div className="card">
        <div className="text-sm font-semibold text-slate-700">
          Rate history
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Most recent changes to the mileage rate. Older rows before the audit
          started capturing prior values will only show the new rate.
        </p>
        {rateHistory === null ? (
          <div className="mt-3 text-xs text-slate-500">Loading history…</div>
        ) : rateHistory.length === 0 ? (
          <div className="mt-3 text-xs text-slate-500">
            No rate changes recorded yet.
          </div>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100 text-sm">
            {rateHistory.map((r) => {
              const a = r.after?.value;
              const b = r.before?.value;
              return (
                <li key={r.id} className="py-2">
                  <div className="font-medium tabular-nums text-slate-900">
                    {typeof b === "number"
                      ? `$${b.toFixed(3)} → $${typeof a === "number" ? a.toFixed(3) : "?"}/mile`
                      : typeof a === "number"
                      ? `Set to $${a.toFixed(3)}/mile`
                      : "(no recorded value)"}
                  </div>
                  <div className="text-xs text-slate-500">
                    {formatAuditTime(r.createdAt)} ·{" "}
                    {r.actorName || r.actorEmail || "(unknown admin)"}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="card">
        <div className="text-sm font-semibold text-slate-700">
          Form template
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Current template: <code>{cfg.templatePath2026}</code>. Template upload
          is a future admin feature — drop a new .xlsx into <code>server/templates/</code>
          and update <code>app_config</code> for now.
        </p>
      </div>
    </div>
  );
}
